/**
 * Per-round upload back-off. If the phone dies (e.g. out of memory) while
 * uploading a round, the next launch finds an "attempt" marker and counts one
 * death for that round. Three deaths in a row pause automatic attempts for
 * 1 h, then 6 h, then 24 h. Every localStorage access is guarded: a throw
 * behaves as empty storage and the upload proceeds.
 */

export const ATTEMPT_KEY = 'citrus_fieldscout_attempt';
export const BACKOFF_KEY = 'citrus_fieldscout_backoff';
export const BACKOFF_STEPS_MS: readonly number[] = [3_600_000, 21_600_000, 86_400_000];

interface Attempt {
  uuid: string;
  startedAt: number;
}

interface BackoffEntry {
  deaths: number;
  until: number | null;
}

type BackoffMap = Record<string, BackoffEntry>;

const readJson = <T>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const writeJson = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: behave as empty */
  }
};

const removeKey = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
};

const readMap = (): BackoffMap => {
  const map = readJson<BackoffMap>(BACKOFF_KEY);
  return map && typeof map === 'object' ? map : {};
};

/** Writes the attempt marker synchronously, before the upload starts. */
export function beginAttempt(uuid: string, now: number = Date.now()): void {
  const attempt: Attempt = { uuid, startedAt: now };
  writeJson(ATTEMPT_KEY, attempt);
}

/** Clears the marker and resets the round's count. Call on success and on any orderly failure. */
export function endAttempt(uuid: string): void {
  removeKey(ATTEMPT_KEY);
  const map = readMap();
  map[uuid] = { deaths: 0, until: null };
  writeJson(BACKOFF_KEY, map);
}

/** Runs once per launch: a leftover marker means the last attempt died, so count it. */
export function recordDeathAtLaunch(now: number = Date.now()): void {
  const attempt = readJson<Attempt>(ATTEMPT_KEY);
  if (!attempt || typeof attempt.uuid !== 'string') return;
  const map = readMap();
  const deaths = (map[attempt.uuid]?.deaths ?? 0) + 1;
  const until = deaths >= 3 ? now + BACKOFF_STEPS_MS[Math.min(deaths - 3, 2)] : null;
  map[attempt.uuid] = { deaths, until };
  writeJson(BACKOFF_KEY, map);
  removeKey(ATTEMPT_KEY);
}

/** True while the round is inside its back-off window. */
export function isBackedOff(uuid: string, now: number = Date.now()): boolean {
  const until = readMap()[uuid]?.until;
  return until !== null && until !== undefined && now < until;
}

/** Test helper: clears both keys. */
export function __resetFieldScoutBackoffForTests(): void {
  removeKey(ATTEMPT_KEY);
  removeKey(BACKOFF_KEY);
}
